import React from 'react';
import { GanttTask, getStatusColor } from '../utils/reportingUtils';

interface GanttChartProps {
    tasks: GanttTask[];
}

const GanttChart: React.FC<GanttChartProps> = ({ tasks }) => {
    // Filter out tasks with invalid dates
    const validTasks = tasks.filter(t => {
        const created = new Date(t.createdAt).getTime();
        const due = t.dueDate ? new Date(t.dueDate).getTime() : 0;
        return !isNaN(created) && !isNaN(due) && due > 0;
    });

    if (validTasks.length === 0) {
        return <p className="text-gray-600 dark:text-gray-400">No tasks with valid due dates available for Gantt chart.</p>;
    }

    // Calculate date range
    const allDates = validTasks.map(t => new Date(t.createdAt).getTime());
    const dueDates = validTasks.map(t => new Date(t.dueDate!).getTime());

    const minDateVal = Math.min(...allDates);
    const maxDateVal = Math.max(...dueDates);

    if (!isFinite(minDateVal) || !isFinite(maxDateVal)) {
        return <p className="text-gray-600 dark:text-gray-400">Error calculating date range.</p>;
    }

    const minDate = new Date(minDateVal);
    const maxDate = new Date(maxDateVal);

    // Ensure totalDays is at least 1 to avoid division by zero issues, though logic below handles it
    const totalDays = Math.max(1, Math.ceil((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)) + 1);

    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const calculateBarPosition = (createdAt: string, dueDate: string): { left: number; width: number } => {
        const start = new Date(createdAt).getTime();
        const end = new Date(dueDate).getTime();

        if (isNaN(start) || isNaN(end)) return { left: 0, width: 0 };

        const startOffset = (start - minDate.getTime()) / (1000 * 60 * 60 * 24);
        const duration = (end - start) / (1000 * 60 * 60 * 24);

        return {
            left: (startOffset / totalDays) * 100,
            width: Math.max((duration / totalDays) * 100, 1), // Minimum 1% width
        };
    };

    // Group tasks by project
    const tasksByProject = validTasks.reduce((acc, task) => {
        if (!acc[task.projectId]) {
            acc[task.projectId] = {
                projectName: task.projectName,
                tasks: [],
            };
        }
        acc[task.projectId].tasks.push(task);
        return acc;
    }, {} as { [key: string]: { projectName: string; tasks: GanttTask[] } });

    return (
        <div className="mt-4">
            <h3>Gantt Chart</h3>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Timeline: {formatDate(minDate.toISOString())} - {formatDate(maxDate.toISOString())} ({totalDays} days)
            </div>

            <div className="overflow-x-auto border border-gray-300 dark:border-gray-600 rounded">
                {Object.entries(tasksByProject).map(([projectId, { projectName, tasks: projectTasks }]) => (
                    <div key={projectId} className="mb-6 border-b-2 border-gray-200 dark:border-gray-700 pb-4">
                        <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 font-bold text-gray-900 dark:text-white border-l-4 border-green-500">
                            {projectName}
                        </div>

                        {projectTasks.map(task => {
                            const { left, width } = calculateBarPosition(task.createdAt, task.dueDate!);
                            const statusColor = getStatusColor(task.status);
                            const isOverdue = task.status !== 'completed' && new Date(task.dueDate!) < new Date();

                            return (
                                <div key={task.id} className={`px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center ${isOverdue ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-white dark:bg-gray-900'}`}>
                                    <div className="w-64 flex-shrink-0 pr-4">
                                        <div className="font-medium text-sm">{task.title}</div>
                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                            {task.assignedToName ? `Assigned to: ${task.assignedToName}` : 'Unassigned'}
                                        </div>
                                    </div>

                                    <div className="flex-1 relative h-8 min-w-96">
                                        <div className="absolute h-6 rounded flex items-center justify-center text-white text-xs font-medium shadow-md border-2 border-transparent whitespace-nowrap overflow-visible z-10"
                                             style={{
                                                 left: `${left}%`,
                                                 width: `${width}%`,
                                                 backgroundColor: statusColor,
                                                 borderColor: isOverdue ? '#f44336' : 'transparent',
                                                 textShadow: '0 0 2px rgba(0,0,0,0.5)',
                                             }}>
                                            <span className="px-1">
                                                {task.status.replace('_', ' ')}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="w-24 flex-shrink-0 text-right text-xs text-gray-600 dark:text-gray-400">
                                        Due: {formatDate(task.dueDate!)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            <div className="mt-4 flex gap-4 text-sm">
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-green-500 rounded"></div>
                    <span>Completed</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-blue-500 rounded"></div>
                    <span>In Progress</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-orange-500 rounded"></div>
                    <span>To Do</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-orange-50 dark:bg-orange-900/20 border border-red-500 rounded"></div>
                    <span>Overdue</span>
                </div>
            </div>
        </div>
    );
};

export default GanttChart;
